import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { NotificationManager } from "react-notifications";
import { getErrorFromResponse } from "../../../../../../../../utility/utils";
import { CreacionTrasladoApi } from "../api/CreacionTrasladoApi";
import { useSobranteTraslado } from "./useSobranteTraslado";
import { useFechaHoraReloj } from "../../../../../ProduccionV2/hooks/useFechaHoraReloj";
import { CONTEXTO_RELOJ_BODEGA } from "../../../../../ProduccionV2/components/RelojDesfase/contextosReloj";

export const useTrasladoForm = (navigateToBoleta) => {
    const {
        crearTraslado,
        getExistenciasDisponibles,
        getPalletsIngresadosDespues,
        cargando,
    } = CreacionTrasladoApi();

    // el traslado se registra con la fecha y hora del reloj de bodega
    const relojBodega = useFechaHoraReloj(CONTEXTO_RELOJ_BODEGA);

    const { control: formControl, watch: formWatch } = useForm();

    const destinoTraslado = formWatch("destino_es_conforme_bodega");
    const destinoEsConforme = destinoTraslado
        ? destinoTraslado.value
        : undefined;

    const sobranteTraslado = useSobranteTraslado(destinoEsConforme);

    const [filas, setFilas] = useState([]);
    const [cantidadResultados, setCantidadResultados] = useState(0);
    const [paginaSeleccionada, setPaginaSeleccionada] = useState(1);
    const [search, setSearch] = useState("");

    // mapa pallet_id -> pacas a trasladar (persiste al cambiar de página)
    const [valoresATrasladar, setValoresATrasladar] = useState({});

    // mapa pallet_id -> número de tarjeta (persiste al cambiar de página)
    const [tarjetasSalida, setTarjetasSalida] = useState({});

    const GetExistencias = async () => {
        try {
            const params = {
                page: paginaSeleccionada,
                destino_es_conforme_bodega: destinoEsConforme,
            };
            if (search) params.search = search;

            const response = await getExistenciasDisponibles(params);
            setFilas(response.results || []);
            setCantidadResultados(response.count || 0);
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al obtener las existencias de bodega",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
            setFilas([]);
            setCantidadResultados(0);
        }
    };

    // sin tipo de traslado seleccionado no hay un origen que consultar
    useEffect(() => {
        if (destinoEsConforme === undefined) {
            setFilas([]);
            setCantidadResultados(0);
            return;
        }
        GetExistencias();
    }, [paginaSeleccionada, search, destinoEsConforme]);

    // al cambiar la dirección del traslado, los valores ingresados y la página dejan de tener sentido
    useEffect(() => {
        setValoresATrasladar({});
        setTarjetasSalida({});
        setPaginaSeleccionada(1);
    }, [destinoEsConforme]);

    // pallets del lado de origen que no aparecen en las existencias porque
    // ingresaron a bodega después de la fecha y hora del reloj de bodega
    const [palletsOcultos, setPalletsOcultos] = useState([]);
    const consultaPalletsOcultos = useRef(0);

    useEffect(() => {
        consultaPalletsOcultos.current += 1;
        const consulta = consultaPalletsOcultos.current;

        setPalletsOcultos([]);

        if (destinoEsConforme === undefined) return;

        getPalletsIngresadosDespues({
            destino_es_conforme_bodega: destinoEsConforme,
        }).then((pallets) => {
            if (consulta !== consultaPalletsOcultos.current) return;
            setPalletsOcultos(pallets);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinoEsConforme]);

    const handleSearch = (valor) => {
        setSearch(valor);
        setPaginaSeleccionada(1);
    };

    const handleChangeATrasladar = (palletId, valor) => {
        setValoresATrasladar((prev) => ({
            ...prev,
            [palletId]: valor,
        }));
    };

    const handleChangeTarjetaSalida = (palletId, valor) => {
        setTarjetasSalida((prev) => ({
            ...prev,
            [palletId]: valor,
        }));
    };

    const totalPacasATrasladar = Object.values(valoresATrasladar).reduce(
        (acc, valor) => acc + (Number(valor) > 0 ? Number(valor) : 0),
        0,
    );

    const hayCantidadesIngresadas =
        totalPacasATrasladar > 0 || sobranteTraslado.totalKgATrasladar > 0;

    const FormSubmit = async () => {
        if (!destinoTraslado) {
            NotificationManager.warning(
                "Debe seleccionar un tipo de traslado",
                "Advertencia",
                3000,
            );
            return;
        }

        const filasATrasladar = Object.entries(valoresATrasladar)
            .filter(([, valor]) => Number(valor) > 0)
            .map(([palletId, valor]) => ({
                pallet_id: Number(palletId),
                pacas_a_trasladar: Number(valor),
                tarjeta_salida: (tarjetasSalida[palletId] || "").trim() || null,
            }));

        const filasSobranteATrasladar =
            sobranteTraslado.obtenerFilasATrasladar();

        if (
            filasATrasladar.length === 0 &&
            filasSobranteATrasladar.length === 0
        ) {
            NotificationManager.warning(
                "Debe ingresar al menos una cantidad a trasladar",
                "Advertencia",
                3000,
            );
            return;
        }

        const faltaTarjeta = filasATrasladar
            .concat(filasSobranteATrasladar)
            .some((fila) => !fila.tarjeta_salida);

        if (faltaTarjeta) {
            NotificationManager.warning(
                "Debe indicar el número de tarjeta para cada pallet o sobrante con movimiento",
                "Advertencia",
                3000,
            );
            return;
        }

        const body = {
            destino_es_conforme_bodega: destinoTraslado.value,
            filas: filasATrasladar,
            filas_sobrante: filasSobranteATrasladar,
        };

        try {
            const traslado = await crearTraslado(body);

            NotificationManager.success(
                "Traslado creado con éxito",
                "Éxito",
                3000,
            );

            navigateToBoleta(traslado.id);
        } catch (error) {
            const errorMsg = getErrorFromResponse(
                error,
                "Error al crear el traslado",
            );
            NotificationManager.error(errorMsg, "Error", 3000);
        }
    };

    return {
        formControl,
        destinoTraslado,
        sobranteTraslado,
        hayCantidadesIngresadas,
        palletsOcultos,
        fechaHoraBodega: relojBodega.obtenerFechaHora(),

        filas,
        cantidadResultados,
        paginaSeleccionada,
        setPaginaSeleccionada,
        search,
        handleSearch,

        valoresATrasladar,
        handleChangeATrasladar,
        tarjetasSalida,
        handleChangeTarjetaSalida,
        totalPacasATrasladar,

        FormSubmit,
        cargando: cargando || sobranteTraslado.cargando,
    };
};
